import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Select as SelectPrimitive } from 'radix-ui';
import type { ReactNode } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';

import { cn } from '@/shared/lib/cn';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './Form';

type SelectOption =
  | string
  | {
      disabled?: boolean;
      label: ReactNode;
      value: string;
    };

type SelectOptionGroup = {
  label: ReactNode;
  options: SelectOption[];
};

type SelectProps = React.ComponentProps<typeof SelectPrimitive.Root> & {
  contentClassName?: string;
  emptyText?: ReactNode;
  loading?: boolean;
  loadingText?: ReactNode;
  optionGroups?: SelectOptionGroup[];
  options?: SelectOption[];
  placeholder?: string;
  size?: 'sm' | 'default' | 'lg';
  triggerClassName?: string;
};

function getOptionValue(option: SelectOption) {
  return typeof option === 'string' ? option : option.value;
}

function getOptionLabel(option: SelectOption) {
  return typeof option === 'string' ? option : option.label;
}

function renderSelectOptions(options: SelectOption[]) {
  return options.map((option) => (
    <SelectItem
      key={getOptionValue(option)}
      value={getOptionValue(option)}
      disabled={typeof option === 'string' ? false : option.disabled}
    >
      {getOptionLabel(option)}
    </SelectItem>
  ));
}

function Select({
  children,
  contentClassName,
  emptyText = 'Ничего не найдено',
  loading,
  loadingText = 'Загрузка...',
  optionGroups,
  options,
  placeholder = loading ? 'Загрузка...' : 'Выберите...',
  size,
  triggerClassName,
  ...props
}: SelectProps) {
  if (!options && !optionGroups) {
    return <SelectPrimitive.Root {...props}>{children}</SelectPrimitive.Root>;
  }

  const flatOptions = options ?? [];
  const groupedOptions = optionGroups ?? [];
  const totalOptions =
    flatOptions.length +
    groupedOptions.reduce((count, group) => count + group.options.length, 0);
  const hasOptions = totalOptions > 0;
  const showLoadingOnly = loading && !hasOptions;

  return (
    <SelectPrimitive.Root
      {...props}
      disabled={props.disabled || showLoadingOnly}
    >
      <SelectTrigger className={triggerClassName} size={size}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className={contentClassName}>
        {showLoadingOnly ? (
          <SelectItem value="__select_loading__" disabled>
            {loadingText}
          </SelectItem>
        ) : hasOptions ? (
          <>
            {groupedOptions.map((group) => (
              <SelectGroup key={String(group.label)}>
                <SelectLabel>{group.label}</SelectLabel>
                {renderSelectOptions(group.options)}
              </SelectGroup>
            ))}
            {flatOptions.length > 0 ? renderSelectOptions(flatOptions) : null}
          </>
        ) : (
          <SelectItem value="__select_empty__" disabled>
            {emptyText}
          </SelectItem>
        )}
      </SelectContent>
    </SelectPrimitive.Root>
  );
}

const SelectGroup = SelectPrimitive.Group;

function SelectValue({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return (
    <SelectPrimitive.Value
      data-slot="select-value"
      className={cn(
        'block min-w-0 max-w-full flex-1 overflow-hidden text-ellipsis whitespace-nowrap [&_*]:overflow-hidden [&_*]:text-ellipsis [&_*]:whitespace-nowrap',
        className
      )}
      {...props}
    />
  );
}

type FormSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<
  SelectProps,
  'defaultValue' | 'name' | 'onOpenChange' | 'onValueChange' | 'value'
> & {
  control?: Control<TFieldValues>;
  formItemClassName?: string;
  label?: ReactNode;
  labelClassName?: string;
  name: TName;
  onOpenChange?: (open: boolean, value: string) => void;
  onValueChange?: (value: string) => void;
};

function FormSelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  formItemClassName,
  label,
  labelClassName,
  name,
  onOpenChange,
  onValueChange,
  triggerClassName,
  ...props
}: FormSelectProps<TFieldValues, TName>) {
  const form = useFormContext<TFieldValues>();
  const formControl = control ?? form?.control;

  if (!formControl) {
    throw new Error(
      'FormSelect should be used within <Form> or receive control'
    );
  }

  return (
    <FormField
      control={formControl}
      name={name}
      render={({ field }) => (
        <FormItem className={formItemClassName}>
          {label ? (
            <FormLabel className={labelClassName}>{label}</FormLabel>
          ) : null}
          <FormControl>
            <Select
              {...props}
              value={field.value || undefined}
              onOpenChange={(open) =>
                onOpenChange?.(open, String(field.value ?? ''))
              }
              onValueChange={(value) => {
                field.onChange(value);
                onValueChange?.(value);
              }}
              triggerClassName={cn('w-full', triggerClassName)}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function SelectTrigger({
  className,
  size = 'default',
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  size?: 'sm' | 'default' | 'lg';
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex w-fit min-w-0 items-center justify-between gap-2 overflow-hidden rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-9 data-[size=default]:text-sm data-[size=lg]:h-10 data-[size=lg]:px-4 data-[size=lg]:text-base data-[size=sm]:h-8 data-[size=sm]:px-2.5 data-[size=sm]:text-xs [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="size-4 text-primary" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  className,
  children,
  position = 'popper',
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  // z-[10050] — слой выпадашек (FLOATING_Z_INDEX): список, открытый из
  // модалки, не должен уходить под её подложку.
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        className={cn(
          'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-[10050] max-h-96 w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md border shadow-md',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className
        )}
        position={position}
        {...props}
      >
        <SelectPrimitive.Viewport
          className={cn(
            'w-full min-w-0 p-0',
            position === 'popper' && 'scroll-my-1'
          )}
        >
          <div
            data-slot="select-scroll-inner"
            className="scrollbar-primary max-h-96 w-full min-w-0 overflow-x-hidden overflow-y-auto p-1 scrollbar-gutter-stable"
          >
            {children}
          </div>
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn('text-muted-foreground px-2 py-1.5 text-xs', className)}
      {...props}
    />
  );
}

function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex min-w-0 w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className
      )}
      {...props}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText className="min-w-0 flex-1 truncate">
        {children}
      </SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn('bg-border pointer-events-none -mx-1 my-1 h-px', className)}
      {...props}
    />
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn(
        'flex cursor-default items-center justify-center py-1',
        className
      )}
      {...props}
    >
      <ChevronUp className="size-4 text-primary" />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn(
        'flex cursor-default items-center justify-center py-1',
        className
      )}
      {...props}
    >
      <ChevronDown className="size-4 text-primary" />
    </SelectPrimitive.ScrollDownButton>
  );
}

export type { SelectOption, SelectOptionGroup };

export {
  FormSelect,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
